import produce from 'immer';
import { v4 as uuid } from 'uuid';
import {
  AssetState,
  OperatorData,
  sceneGraphNodeTypesArray,
  SceneGraphNode,
  SceneGraphNodeType,
  SceneGraphPlugs,
} from '../types';
import addDiff from './addDiff';
import { mapValues } from 'lodash';

type DefaultOperator = string | null;
interface OperatorsDef {
  defaultOp?: DefaultOperator; // Default Operator for the plug when default initialized.

  required?: boolean; // If true, the plug cannot be disabled.
  emptiable?: boolean; // If true, the plug can have 0 operators. If emptiable is false|undefined, then you MUST provide a default operator.
}

type RawOperatorsDef = OperatorsDef | DefaultOperator;

interface RawPlugDef {
  [plugsType: string]: RawOperatorsDef;
}

interface RawNodeDef {
  [nodeType: string]: RawPlugDef;
}

const rawNodeTypes: RawNodeDef = {
  Scene: {
    Environment: 'Environment',
    Player: 'Player',
    Timeline: 'Timeline',
    PostEffect: 'PostEffect',
  },
  Objects: {
    Objects: 'Objects',
    Physics: { emptiable: true },
  },
  MaterialLibrary: {},
  //
  PolyMesh: {
    PolyMesh: 'Box',
    Transform: 'Transform',
    Material: 'Reference',
    Physics: { emptiable: true },
    Properties: 'PolyMeshProperties',
  },
  Light: {
    Light: 'PointLight',
    Transform: 'Transform',
    Properties: 'Default',
  },
  Camera: {
    Camera: 'Camera',
    Transform: 'Transform',
    Properties: 'Default',
  },
  Null: {
    Null: 'Null',
    Transform: 'Transform',
    Physics: { emptiable: true },
    Properties: 'Default',
  },
  Model: {
    Null: 'Model',
    Transform: 'Transform',
    Physics: { emptiable: true },
    Mixer: 'Mixer',
    Properties: 'ModelProperties',
  },
  //
  Material: {
    Material: 'Physical',
    Properties: 'MaterialProperties',
  },
  Image: {
    Image: 'Image',
    Properties: 'ImageProperties',
  },
  //
  Pass: {
    Pass: 'Pass',
  },
  //
  Renderer: {
    Renderer: 'WebGLRenderer',
  },
  Bone: {
    Bone: 'Bone',
    Transform: 'BoneTransform',
    Properties: 'Default',
  },
  //
  Annotation: {
    Annotation: 'Annotation',
    Transform: 'Transform',
    Properties: 'Default',
  },
  Measurement: {
    Measurement: 'Ruler',
    Properties: 'Default',
  },
  //
  Sprite: {
    Sprite: 'Sprite',
    Transform: 'Transform',
    Properties: 'Default',
  },
  //
  Shape: {
    Shape: 'Rectangle',
    Transform: 'Transform',
    Material: 'Reference',
    Properties: 'Default',
  },
  Helper: {
    Null: 'Helper',
    Transform: 'Transform',
    Properties: 'Default',
  },
  Vrscene: {
    Vrscene: 'vrscene',
    Transform: 'Transform',
    Properties: 'Default',
  },
  Item: { Proxy: 'Proxy' },
  Upload: { Proxy: 'Proxy' },
  Group: {
    Group: 'Group',
    Properties: 'GroupProperties',
  },
  Layer: {
    Properties: 'LayerProperties',
  },
  ImageLayer: {
    Properties: 'ImageLayerProperties',
  },
  Composite: {
    Composite: 'Composite',
  },
  ShadowPlane: {
    ShadowPlane: 'ShadowPlane',
    Transform: 'Transform',
    Properties: 'Default',
  },
  Vector: {
    Vector: 'Vector',
  },
  Font: {
    Font: 'Font',
  },
  Video: {
    Video: 'Video',
  },
  Lut: {
    Lut: 'Lut',
  },
  Vfb: {
    Vfb: 'Vfb',
  },
  Stage: {
    Proxy: 'Proxy',
  },
  //
  VrayMesh: {
    VrayMesh: 'VrayMesh',
    Transform: 'Transform',
    Material: 'VrayMeshReference',
    Properties: 'Default',
  },
  VrayLight: {
    VrayLight: 'VrayLight',
    Transform: 'Transform',
    Properties: 'Default',
  },
  //
  LayoutContainer: {
    LayoutContainer: 'MeshLayoutContainer',
    Selection: 'Selection',
    Properties: 'Default',
  },
};

interface PlugDef {
  [plugsType: string]: OperatorsDef;
}

interface NodeDef {
  [nodeType: string]: PlugDef;
}

function rawDefToDef(
  node: string,
  plug: string,
  definition: RawOperatorsDef
): OperatorsDef {
  if (typeof definition === 'string') {
    return {
      defaultOp: definition,

      required: true,
      emptiable: false,
    };
  } else if (definition === null) {
    return {
      defaultOp: undefined,

      required: false,
      emptiable: true,
    };
  } else {
    const { emptiable, defaultOp } = definition;
    // If emptiable is false|undefined, then you MUST provide a default operator.
    if (!(emptiable || defaultOp)) {
      throw new Error(
        `Invalid definition for node type "${node}", plug type "${plug}": a non-emptiable plug must have a default operator.`
      );
    }
    return definition;
  }
}

interface DefaultOperators {
  [plug: string]: string;
}

const nodeShapes = mapValues(
  rawNodeTypes,
  (rawPlug, node) =>
    mapValues(rawPlug, (rawOps, plug) =>
      rawDefToDef(node, plug, rawOps)
    ) as PlugDef
) as NodeDef;

// Initialize plugs object with defaults.
// Does not remove unknown plugs
function initPlugs(nodeType: string, plugs: SceneGraphPlugs = {}) {
  const plugDef = nodeShapes[nodeType];
  if (!plugDef) {
    // unknown node type, ignore
    return plugs;
  }

  // Assign `plugs` so Unknown/Invalid plugs are just taken as-is.
  const toPlugs: SceneGraphPlugs = { ...plugs };

  // Init by mapping over defaults to have a consitent ordering.
  // Cannot use mapObjIndexed because plugs cannot have the value `undefined`, the must be actually undefined.
  for (const plugName of Object.keys(plugDef)) {
    const { required } = plugDef[plugName];
    if (plugs[plugName] || required) {
      toPlugs[plugName] = initPlug(nodeType, plugName, plugs[plugName]);
    }
  }

  return toPlugs;
}

function initPlug(
  nodeType: string,
  plugName: string,
  operators?: OperatorData[]
) {
  const { defaultOp, emptiable } = nodeShapes[nodeType][plugName];

  const plug = operators ?? [];

  if (plug.length === 0 && defaultOp) {
    plug.push({ type: defaultOp, name: defaultOp });
  }

  for (const { type } of plug) {
    if (type == null) {
      throw new Error(`Cannot have empty type for operator.`);
    }
  }

  // Check emptiable is respected
  if (!emptiable && plug.length == 0) {
    throw new Error(`${nodeType} node's "${plugName}" plug cannot be empty`);
  }

  return plug;
}

/**
 *
 * Add a node to the asset state.
 *
 * @param state
 * @param node
 * @param parentId
 * @returns
 */
export default function addNode(
  state: AssetState,
  node: Partial<SceneGraphNode>,
  parentId?: string
): AssetState {
  const { name, type, configurator } = node;
  if (!type) throw new Error('addNode requires type to be set');
  if (!name) throw new Error('addNode requires name to be set');
  if (!sceneGraphNodeTypesArray.includes(type)) {
    throw new Error(`invalid type: ${type} for addNode`);
  }

  if (!node.id) node.id = uuid();
  if (!node.children) node.children = [];
  node.plugs = initPlugs(type, node.plugs);

  return produce(state, (draftState) => {
    const path = parentId ? [parentId, 'children'] : [];
    return addDiff(draftState, { path, value: node, type: 'Node' });
  });
}
